import { z } from 'zod';
import {
    CreateOrderActions,
    CreateOrderData,
    OnApproveActions,
    OnApproveData,
} from '@paypal/paypal-js';
import {
    CardAllValidData,
    CardFieldValidData,
    CardBrandData,
    CardFocusData,
    CardLoadData,
    ValidationError,
    ComponentFocusObject,
    UIElement,
    AdyenCheckoutError,
    CardConfigSuccessData,
    CardBinLookupData,
    CardBinValueData,
    OnChangeData,
} from '@adyen/adyen-web';
import ApplePayValidateMerchantEvent = ApplePayJS.ApplePayValidateMerchantEvent;
import ApplePayPaymentAuthorizedEvent = ApplePayJS.ApplePayPaymentAuthorizedEvent;

export const paypalConfigSchema = z
    .object({
        merchantProfileId: z.string().uuid().optional(),
        storeId: z.string().uuid().optional(),
        clientId: z.string().optional(),
        currency: z.string().optional(),
        createOrderCallback: z
            .function()
            .args(z.custom<CreateOrderData>(), z.custom<CreateOrderActions>())
            .returns(z.promise(z.string()))
            .optional(),
        onApproveCallback: z
            .function()
            .args(z.custom<OnApproveData>(), z.custom<OnApproveActions>())
            .returns(z.promise(z.void()))
            .optional(),
        mountingElement: z.string().optional(),
    })
    .strict();
export type PaypalConfig = z.infer<typeof paypalConfigSchema>;

export const adyenCardConfigSchema = z
    .object({
        // @see https://docs.adyen.com/development-resources/live-endpoints/#checkout-js-endpoints
        environment: z
            .enum([
                'test',
                'live',
                'live-us',
                'live-au',
                'live-apse',
                'live-in',
            ])
            .default('test'),
        merchantProfileId: z.string().uuid().optional(),
        storeId: z.string().uuid().optional(),
        paymentFlow: z.string().optional(),
        cardNameElement: z.string().optional(),
        tokenizePaymentMethod: z.boolean().optional(),
        tokenId: z.string().optional(),
        paymentMethod: z.string().optional(),
        clientKey: z.string().optional(),
        mountingElement: z.string().optional(),
        locale: z.string().optional(),
        countryCode: z.string().optional(),
        showPayButton: z.boolean().optional(),
        translations: z.record(z.any()).optional(),

        // Global Adyen Callbacks
        onChangeCallback: z
            .function()
            .args(z.custom<OnChangeData>(), z.custom<UIElement>())
            .returns(z.promise(z.void()))
            .optional(),
        onEnterKeyPressedCallback: z
            .function()
            .args(z.custom<Element>(), z.custom<UIElement>())
            .returns(z.promise(z.void()))
            .optional(),
        onAllValidCallback: z
            .function()
            .args(z.custom<CardAllValidData>())
            .returns(z.promise(z.void()))
            .optional(),
        onFieldValidCallback: z
            .function()
            .args(z.custom<CardFieldValidData>())
            .returns(z.promise(z.void()))
            .optional(),
        onBrandCallback: z
            .function()
            .args(z.custom<CardBrandData>())
            .returns(z.promise(z.void()))
            .optional(),
        onFocusCallback: z
            .function()
            .args(z.custom<CardFocusData>(), z.custom<ComponentFocusObject>())
            .returns(z.promise(z.void()))
            .optional(),
        onLoadCallback: z
            .function()
            .args(z.custom<CardLoadData>())
            .returns(z.promise(z.void()))
            .optional(),
        onValidationErrorCallback: z
            .function()
            .args(z.custom<ValidationError[]>())
            .returns(z.promise(z.void()))
            .optional(),

        // Card Component Config
        cardComponentBrands: z.array(z.string()).optional(),

        // Card Component Callbacks
        cardComponentOnBrand: z
            .function()
            .args(z.custom<CardBrandData>())
            .returns(z.promise(z.void()))
            .optional(),
        cardComponentOnAllValid: z
            .function()
            .args(z.custom<CardAllValidData>())
            .returns(z.promise(z.void()))
            .optional(),
        cardComponentOnConfigSuccess: z
            .function()
            .args(z.custom<CardConfigSuccessData>())
            .returns(z.promise(z.void()))
            .optional(),
        cardComponentOnFocus: z
            .function()
            .args(z.custom<CardFocusData>())
            .returns(z.promise(z.void()))
            .optional(),
        cardComponentOnBinLookup: z
            .function()
            .args(z.custom<CardBinLookupData>())
            .returns(z.promise(z.void()))
            .optional(),
        cardComponentOnBinValue: z
            .function()
            .args(z.custom<CardBinValueData>())
            .returns(z.promise(z.void()))
            .optional(),
        cardComponentOnEnterKeyPressed: z
            .function()
            .args(z.custom<Element>(), z.custom<UIElement>())
            .returns(z.promise(z.void()))
            .optional(),
        cardComponentOnFieldValid: z
            .function()
            .args(z.custom<CardFieldValidData>())
            .returns(z.promise(z.void()))
            .optional(),
        cardComponentOnValidationError: z
            .function()
            .args(z.custom<ValidationError[]>())
            .returns(z.promise(z.void()))
            .optional(),

        // set as optional, but we ensure that this is set in the code later
        cardComponentStyles: z
            .union([z.record(z.any()), z.undefined()])
            .optional(),
        cardComponentAutoFocus: z.boolean().optional(),

        onErrorCallback: z
            .function()
            .args(z.custom<AdyenCheckoutError>(), z.custom<UIElement>())
            .returns(z.promise(z.void()))
            .optional(),
    })
    .strict();
export type AdyenCardConfig = z.infer<typeof adyenCardConfigSchema>;

export const samsungPayConfigSchema = z
    .object({
        merchantProfileId: z.string().uuid().optional(),
        storeId: z.string().uuid().optional(),
        merchantAccountName: z.string().optional(),
        invoiceId: z.string().optional(),
        samsungPayServiceId: z.string().optional(),
        paymentReference: z.string().optional(),
        environment: z.string().optional(),
        paymentFlow: z.string().optional(),
        samsungPayCurrency: z.string().optional().default('USD'),
        samsungPayAmount: z.string().optional(),
        onInitializeTransaction: z.function().optional(),
        mountingElement: z.string().optional(),
        buttonType: z.string().optional().default('buy'),
        locale: z.string().optional().default('en-US'),
        countryCode: z.string().optional().default('US'),
        supportedNetworks: z
            .array(z.string())
            .optional()
            .default(['visa', 'masterCard', 'amex', 'discover']),
        protocol: z.string().optional().default('PROTOCOL_3DS'),
        label: z.string().optional().default('Panasonic Avionics'),
        buttonColor: z.string().optional().default('black'),
    })
    .strict();

export const applePayConfigSchema = z
    .object({
        merchantProfileId: z.string().uuid().optional(),
        storeId: z.string().uuid().optional(),
        paymentFlow: z.string().optional(),

        applePayCurrency: z.string().optional().default('USD'),
        applePayAmount: z.string().optional(),

        onValidateMerchantCallback: z
            .function()
            .args(z.custom<ApplePayValidateMerchantEvent>())
            .returns(
                z.promise(
                    z
                        .object({ merchantSessionIdentifier: z.string() })
                        .passthrough(),
                ),
            )
            .optional(),

        onPaymentAuthorizedCallback: z
            .function()
            .args(z.custom<ApplePayPaymentAuthorizedEvent>())
            .returns(z.boolean())
            .optional(),

        mountingElement: z.string().optional(),
        buttonType: z.string().optional().default('buy'),
        locale: z.string().optional().default('en-US'),
        countryCode: z.string().optional().default('US'),
        supportedNetworks: z
            .array(z.string())
            .optional()
            .default(['visa', 'masterCard', 'amex', 'discover']),
        merchantCapabilities: z
            .array(z.string())
            .optional()
            .default(['supports3DS']),
        label: z.string().optional().default('Panasonic Avionics'),
    })
    .strict();
export type ApplePayConfig = z.infer<typeof applePayConfigSchema>;
export type SamsungPayConfig = z.infer<typeof samsungPayConfigSchema>;

export const googlePayConfigSchema = z
    .object({
        merchantAccountName: z.string().optional(),
        environment: z.string().optional(),
        merchantProfileId: z.string().uuid().optional(),
        storeId: z.string().uuid().optional(),
        paymentFlow: z.string().optional(),

        buttonColor: z.string().optional().default('default'),
        buttonType: z.string().optional().default('buy'),
        buttonRadius: z.number().optional().default(0),
        buttonBorderType: z.string().optional().default('default_border'),
        buttonSizeMode: z.string().optional().default('fill'),
        buttonLocale: z.string().optional().default('en'),

        googlePayCurrency: z.string().optional().default('USD'),
        googlePayAmount: z.string().optional(),

        onInitializeTransaction: z.function().optional(),

        mountingElement: z.string().optional(),

        locale: z.string().optional().default('en-US'),
        countryCode: z.string().optional().default('US'),
        supportedNetworks: z
            .array(z.string())
            .optional()
            .default(['visa', 'masterCard', 'amex', 'discover']),
        merchantCapabilities: z
            .array(z.string())
            .optional()
            .default(['supports3DS']),
        label: z.string().optional().default('Panasonic Avionics'),

        merchantId: z.string().optional(),
        merchantName: z.string().optional().default('Panasonic Avionics'),
        gateway: z.string().optional(),
        offers: z
            .array(
                z.object({
                    redemptionCode: z.string().optional(),
                    description: z.string().optional(),
                }),
            )
            .optional(),
    })
    .strict();
export type GooglePayConfig = z.infer<typeof googlePayConfigSchema>;

export const airwallexCardConfigSchema = z
    .object({
        environment: z.enum(['test', 'prod']).default('test'),
        merchantProfileId: z.string().uuid().optional(),
        storeId: z.string().uuid().optional(),
        invoiceId: z.string().optional(),
        paymentReference: z.string().optional(),
        airwallexAmount: z.string().optional(),
        airwallexCurrency: z.string().optional(),
        mountingElement: z.string().optional(),
        cardNumberElement: z.string().optional(),
        expiryElement: z.string().optional(),
        cvcElement: z.string().optional(),
        chargeButtonElement: z.string().optional(),
        autoCapture: z.boolean().optional(),
        onReady: z.function().optional(),
        onChange: z.function().optional(),
        onError: z.function().optional(),
        onSubmit: z.function().optional(),
        onInitializeTransaction: z
            .function()
            .args(
                z.object({
                    amount: z.object({
                        value: z.string(),
                        currency: z.string(),
                    }),
                    invoiceId: z.string(),
                    storeId: z.string(),
                    merchantProfileId: z.string(),
                    authToken: z.string(),
                    idempotencyKey: z.string(),
                    apiUrl: z.string(),
                }),
            )
            .returns(z.promise(z.any()))
            .optional(),
    })
    .strict();
export type AirwallexCardConfig = z.infer<typeof airwallexCardConfigSchema>;

export const prepareConfigSchema = z
    .object({
        apiUrl: z.string().url().optional(),
        paymentJwt: z.string().jwt().optional(),
        nextCloudToken: z.string().optional(),
        idempotencyKey: z.string().uuid().optional(),

        ADYEN_CARD: adyenCardConfigSchema.optional(),
        PAYPAL: paypalConfigSchema.optional(),
        ADYEN_APPLEPAY: applePayConfigSchema.optional(),
        ADYEN_SAMSUNGPAY: samsungPayConfigSchema.optional(),
        ADYEN_GOOGLEPAY: googlePayConfigSchema.optional(),
        AIRWALLEX_CARD: airwallexCardConfigSchema.optional(),
    })
    .strict();
export type PrepareConfig = z.infer<typeof prepareConfigSchema>;
